import React, { useState, useEffect } from "react";
import { ETestingTaskState, ESuiteMode } from "../../../../Model/ModuleState";
import ImagerManualControl, {
  IMAGER_MANUAL_ACCORDION_STATE,
} from "../../../Imager/ImagerManualControl";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import {
  getOneImagerCapabilities,
  getImagerCapabilities,
} from "../../../../redux/reducers/ImagerCapabilitiesReducer";
import TabPanel from "../../../PlateTypeEditor/TabPanel";
import CommonDropDown from "../../../Common/DropDown/CommonDropDown";
import ImagePlateTest from "./ImagePlateTest";
import PlateBumpTest from "./PlateBumpTest";
import AutoExposureTest from "./AutoExposureTest";
import LightPathSwitchingTest from "./LightPathSwitchingTest";
import ObjectiveSwitchingTest from "./ObjectiveSwitchingTest";
import CondenserTest from "./CondenserTest";
import "../Testing.css";
import {
  getSuiteMode,
  getModuleState,
} from "../../../../redux/reducers/ModuleStateReducer";
import ImagerUtil from "../../../../Utility/ImagerUtil";
import PlateTypeCaptureProfileApi from "../../../../api/PlateTypeCaptureProfileApi";
import { ELightPath } from "../../../../Model/CaptureProfile";

const getCategoryDisplayValues = (
  showCameraSwitching: boolean,
  showLensSwitching: boolean
) => {
  let categoryDisplayValues = [
    {
      display: "Imaging Testing",
      value: ETestingTaskState.Imaging,
      disabled: false,
    },
    {
      display: "Plate Bump Testing",
      value: ETestingTaskState.PlateBump,
      disabled: false,
    },
    {
      display: "Condenser Testing",
      value: ETestingTaskState.Condenser,
      disabled: false,
    },
    {
      display: "Auto Exposure Testing",
      value: ETestingTaskState.AutoExposure,
      disabled: false,
    },
  ];

  if (showCameraSwitching) {
    categoryDisplayValues.push({
      display: "Camera Switching Testing",
      value: ETestingTaskState.LightpathSwitching,
      disabled: false,
    });
  }

  if (showLensSwitching) {
    categoryDisplayValues.push({
      display: "Lens Switching Testing",
      value: ETestingTaskState.ObjectiveSwitching,
      disabled: false,
    });
  }

  return categoryDisplayValues;
};

function getInitialChevronButtonDegree(key: string): number {
  const value = localStorage.getItem(key);
  if (value != null) {
    const objVal = JSON.parse(value) as boolean[];
    if (objVal.every((val) => val)) {
      return 180;
    }
    return 0;
  } else {
    return 0;
  }
}

type Props = {
  activeCategory: ETestingTaskState;
  setActiveCategory: (value: ETestingTaskState) => void;
  imagerIdentifier: string;
};

export default function TestingImagerCommandPanel({
  activeCategory,
  setActiveCategory,
  imagerIdentifier,
}: Props) {
  const suiteMode = useTypedSelector((state) => getSuiteMode(state));
  const [triggerExpandSetting, setTriggerExpandSetting] = useState(false);
  const [chevronButtonDegree, setChevronButtonDegree] = useState(
    getInitialChevronButtonDegree(IMAGER_MANUAL_ACCORDION_STATE)
  );
  const imagerCapabilitiesCollection = useTypedSelector((state) =>
    getImagerCapabilities(state)
  );

  const imagerModuleState = useTypedSelector((state) =>
    getModuleState(state, imagerIdentifier)
  );

  const imagerState = imagerModuleState.imager;

  const imagerIdentifiersWithObjectiveZoom =
    ImagerUtil.getImagerIdentifiersWithObjective(imagerCapabilitiesCollection);
  const totalLightpath = ImagerUtil.getTotalLightpathCount(
    imagerCapabilitiesCollection
  );

  const showLensSwitching = imagerIdentifiersWithObjectiveZoom.length > 0;
  const showCameraSwitching = totalLightpath > 1;
  const isDisabled = suiteMode !== ESuiteMode.Normal;

  const onAllChildrenCollapsed = (collapsed: boolean) => {
    if (collapsed) {
      setChevronButtonDegree(0);
    } else {
      setChevronButtonDegree(180);
    }
  };

  const imagerCapabilities = useTypedSelector((state) =>
    getOneImagerCapabilities(state, imagerIdentifier)
  );
  const [disabledLightPaths, setDisabledLightPaths] = useState(
    imagerCapabilities.lightpathCollection.allIds
  );

  const plate = imagerState?.plate;

  useEffect(() => {
    let isMounted = true;
    if (plate !== null && plate !== undefined) {
      PlateTypeCaptureProfileApi.getPlateTypeCaptureProfiles(
        plate.plateType.id,
        imagerIdentifier
      ).then((plateTypeCaptureProfiles) => {
        if (isMounted) {
          // Only update state if component is still mounted
          let disabledLightPaths: ELightPath[] = [];
          for (const plateTypeCaptureProfile of plateTypeCaptureProfiles) {
            if (!plateTypeCaptureProfile.enabled) {
              disabledLightPaths.push(plateTypeCaptureProfile.lightpath);
            }
          }
          setDisabledLightPaths(disabledLightPaths);
        }
      });
    } else {
      setDisabledLightPaths([]);
    }

    return () => {
      isMounted = false; // Cleanup function sets the flag to false
    };
  }, [plate]);

  return (
    <div
      style={{
        backgroundColor: "white",
        height: "100%",
        overflow: "auto",
        fontSize: 14,
        WebkitBoxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        MozBoxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
      }}
    >
      <TabPanel
        id="testing-imager-tab"
        collapsible={false}
        disabled={false}
        allowChange={!isDisabled}
        activeTabStyle={{ padding: 0 }}
        chevronButton="double"
        onTriggerExpand={() => setTriggerExpandSetting(true)}
        chevronButtondegree={chevronButtonDegree}
        style={{ height: "100%" }}
      >
        {{
          Testing: (
            <div className="testing-panel">
              <div className="testing-category">
                <label>Testing Option :</label>
                <CommonDropDown
                  id="testing-imager-selector"
                  value={activeCategory}
                  onChange={setActiveCategory}
                  dropDownDisplayValues={getCategoryDisplayValues(
                    showCameraSwitching,
                    showLensSwitching
                  )}
                  style={{ height: 24, width: 384 }}
                  disabled={isDisabled}
                />
              </div>
              {activeCategory === ETestingTaskState.Imaging && (
                <ImagePlateTest imagerIdentifier={imagerIdentifier} />
              )}
              {activeCategory === ETestingTaskState.PlateBump &&
                imagerCapabilitiesCollection.byId[imagerIdentifier]
                  ?.isPlateBumpSupported && (
                  <PlateBumpTest imagerIdentifier={imagerIdentifier} />
                )}
              {activeCategory === ETestingTaskState.AutoExposure && (
                <AutoExposureTest imagerIdentifier={imagerIdentifier} />
              )}
              {activeCategory === ETestingTaskState.LightpathSwitching && (
                <LightPathSwitchingTest imagerIdentifier={imagerIdentifier} />
              )}
              {activeCategory === ETestingTaskState.ObjectiveSwitching && (
                <ObjectiveSwitchingTest imagerIdentifier={imagerIdentifier} />
              )}
              {activeCategory === ETestingTaskState.Condenser && (
                <CondenserTest imagerIdentifier={imagerIdentifier} />
              )}
            </div>
          ),
          "Control Panel": (
            <ImagerManualControl
              wellLimitEnabled={true}
              imagerIdentifier={imagerIdentifier}
              disabledLightPaths={disabledLightPaths}
              headerEnabled={false}
              triggerExpandAll={triggerExpandSetting}
              setTriggerExpandAll={setTriggerExpandSetting}
              onAllItemCollapsed={onAllChildrenCollapsed}
            />
          ),
        }}
      </TabPanel>
    </div>
  );
}
